import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ClockIcon } from '@untitledui-icons/react/line'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { Checkbox } from '@/components/ui/Checkbox'
import { cn } from '@/lib/utils'
import { getWeekdayName } from '@/lib/dateFormat'
import type { WorkingHoursRule } from './WorkingHoursCard'

const DAY_IDS = [1, 2, 3, 4, 5, 6, 7]

const DEFAULT_RULE: WorkingHoursRule = {
  mode: 'client_first',
  recurring: true,
  recurrence_pattern: {
    days: [1, 2, 3, 4, 5],
    start_time: '09:00',
    end_time: '17:00'
  },
  forward_number: '',
  dial_timeout: 10
}

interface WorkingHoursModalProps {
  isOpen: boolean
  onClose: () => void
  rule: WorkingHoursRule | null
  index: number
  onSave: (rule: WorkingHoursRule, index: number) => void
  assistantPhoneNumber?: string
}

interface ValidationErrors {
  forward_number?: string
  dial_timeout?: string
  days?: string
  start_time?: string
  end_time?: string
  start_date?: string
  end_date?: string
}

export function WorkingHoursModal({ isOpen, onClose, rule, index, onSave, assistantPhoneNumber }: WorkingHoursModalProps) {
  const { t } = useTranslation()
  const [currentRule, setCurrentRule] = useState<WorkingHoursRule>(DEFAULT_RULE)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [is24HourMode, setIs24HourMode] = useState(false)

  useEffect(() => {
    if (rule) {
      setCurrentRule({ ...DEFAULT_RULE, ...rule })
      // Check if 24-hour mode
      if (rule.recurring && 
          rule.recurrence_pattern?.start_time === '00:00' && 
          rule.recurrence_pattern?.end_time === '23:59') {
        setIs24HourMode(true)
      } else {
        setIs24HourMode(false)
      }
    } else {
      setCurrentRule(DEFAULT_RULE)
      setIs24HourMode(false)
    }
    setErrors({})
  }, [rule, isOpen])

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {}

    if (!currentRule.forward_number) {
      newErrors.forward_number = t('resources.assistants.working_hours.validation.phone_required')
    } else if (!/^\+[1-9]\d{1,14}$/.test(currentRule.forward_number)) {
      newErrors.forward_number = t('resources.assistants.working_hours.validation.phone_invalid')
    }

    if (!currentRule.dial_timeout) {
      newErrors.dial_timeout = t('resources.assistants.working_hours.validation.dial_timeout_required')
    } else if (currentRule.dial_timeout < 1 || currentRule.dial_timeout > 60) {
      newErrors.dial_timeout = t('resources.assistants.working_hours.validation.dial_timeout_range')
    }

    if (currentRule.recurring) {
      if (!currentRule.recurrence_pattern?.days?.length) {
        newErrors.days = t('resources.assistants.working_hours.validation.days_required')
      }
    } else {
      if (!currentRule.start_date) {
        newErrors.start_date = t('resources.assistants.working_hours.validation.start_date_required')
      }
      if (!currentRule.end_date) {
        newErrors.end_date = t('resources.assistants.working_hours.validation.end_date_required')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) return

    const formattedRule: WorkingHoursRule = {
      mode: currentRule.mode,
      recurring: currentRule.recurring,
      forward_number: currentRule.forward_number,
      dial_timeout: currentRule.dial_timeout,
      ...(currentRule.caller_id_mode === 'assistant' && { caller_id_mode: 'assistant' }),
    }

    if (currentRule.recurring) {
      formattedRule.recurrence_pattern = {
        days: currentRule.recurrence_pattern?.days || [],
        start_time: currentRule.recurrence_pattern?.start_time || '09:00',
        end_time: currentRule.recurrence_pattern?.end_time || '17:00'
      }
    } else {
      formattedRule.start_date = currentRule.start_date
      formattedRule.end_date = currentRule.end_date
      formattedRule.start_time = currentRule.recurrence_pattern?.start_time || '00:00'
      formattedRule.end_time = currentRule.recurrence_pattern?.end_time || '23:59'
    }

    onSave(formattedRule, index)
    onClose()
  }

  const handleDayToggle = (day: number) => {
    const currentDays = currentRule.recurrence_pattern?.days || []
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort((a, b) => a - b)
    
    setCurrentRule({
      ...currentRule,
      recurrence_pattern: {
        ...currentRule.recurrence_pattern!,
        days: newDays
      }
    })
  }

  const handleTimeChange = (type: 'start_time' | 'end_time', value: string) => {
    setCurrentRule({
      ...currentRule,
      recurrence_pattern: {
        ...currentRule.recurrence_pattern!,
        [type]: value
      }
    })
  }

  const set24HourMode = () => {
    setIs24HourMode(true)
    setCurrentRule({
      ...currentRule,
      recurrence_pattern: {
        ...currentRule.recurrence_pattern!,
        start_time: '00:00',
        end_time: '23:59'
      }
    })
  }

  const disable24HourMode = () => {
    setIs24HourMode(false)
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="4xl">
      <ModalContent>
        <ModalHeader onClose={onClose}>
          {index >= 0 ? t('resources.assistants.working_hours.edit') : t('resources.assistants.working_hours.add')}
        </ModalHeader>
        
        <ModalBody className="max-h-[60vh] overflow-y-auto space-y-6">
          {/* Mode selection */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('resources.assistants.working_hours.mode')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('resources.assistants.working_hours.mode_description')}
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
                <input
                  type="radio"
                  name="mode"
                  value="client_first"
                  checked={currentRule.mode === 'client_first'}
                  onChange={() => setCurrentRule({ ...currentRule, mode: 'client_first' })}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{t('resources.assistants.working_hours.mode_client_first')}</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('resources.assistants.working_hours.mode_client_first_description')}
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
                <input
                  type="radio"
                  name="mode"
                  value="client_only"
                  checked={currentRule.mode === 'client_only'}
                  onChange={() => setCurrentRule({ ...currentRule, mode: 'client_only' })}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{t('resources.assistants.working_hours.mode_client_only')}</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('resources.assistants.working_hours.mode_client_only_description')}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Forward number */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('resources.assistants.working_hours.forward_to')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('resources.assistants.working_hours.forward_to_description')}
            </p>
            <Input
              value={currentRule.forward_number}
              onChange={(value) => setCurrentRule({ ...currentRule, forward_number: value })}
              placeholder="+31612345678"
            />
            {errors.forward_number && (
              <p className="mt-1 text-sm text-error-600">{errors.forward_number}</p>
            )}
          </div>

          {/* Dial timeout (only for client_first) */}
          {currentRule.mode === 'client_first' && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                {t('resources.assistants.working_hours.dial_timeout')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('resources.assistants.working_hours.dial_timeout_description')}
              </p>
              <Input
                type="number"
                value={currentRule.dial_timeout.toString()}
                onChange={(value) => setCurrentRule({ ...currentRule, dial_timeout: parseInt(value) || 10 })}
                min={1}
                max={60}
              />
              {errors.dial_timeout && (
                <p className="mt-1 text-sm text-error-600">{errors.dial_timeout}</p>
              )}
            </div>
          )}

          {/* Caller ID mode */}
          <div className={cn(!assistantPhoneNumber && 'opacity-50')}>
            <div className="flex items-center gap-3">
              <Checkbox
                isSelected={currentRule.caller_id_mode === 'assistant'}
                onChange={(checked) => setCurrentRule({ ...currentRule, caller_id_mode: checked ? 'assistant' : 'caller' })}
                isDisabled={!assistantPhoneNumber}
              />
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('resources.assistants.answer_schedule.caller_id_assistant')}
                  {assistantPhoneNumber && (
                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                      ({assistantPhoneNumber})
                    </span>
                  )}
                </span>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {assistantPhoneNumber
                    ? t('resources.assistants.answer_schedule.caller_id_mode_description')
                    : t('resources.assistants.answer_schedule.caller_id_no_phone')
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Recurring toggle */}
          <div className="flex items-center gap-3">
            <Checkbox
              isSelected={currentRule.recurring}
              onChange={(checked) => setCurrentRule({ ...currentRule, recurring: checked })}
            />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.working_hours.recurring_schedule')}
            </span>
          </div>

          {/* Schedule configuration */}
          {currentRule.recurring ? (
            <>
              {/* Days selection */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {t('resources.assistants.working_hours.days')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {DAY_IDS.map(dayId => (
                    <button
                      key={dayId}
                      type="button"
                      onClick={() => handleDayToggle(dayId)}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        currentRule.recurrence_pattern?.days?.includes(dayId)
                          ? 'bg-brand-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                      )}
                    >
                      {getWeekdayName(dayId, 'short')}
                    </button>
                  ))}
                </div>
                {errors.days && (
                  <p className="mt-1 text-sm text-error-600">{errors.days}</p>
                )}
              </div>

              {/* Time selection */}
              <div>
                <div className="mb-3">
                  {is24HourMode ? (
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-brand-500" />
                      <span className="text-sm text-brand-600 dark:text-brand-400">
                        {t('resources.assistants.working_hours.active_24h')}
                      </span>
                      <Button
                        color="link-color"
                        size="sm"
                        onPress={disable24HourMode}
                        className="text-sm"
                      >
                        {t('resources.assistants.working_hours.customize')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      color="secondary"
                      size="sm"
                      onPress={set24HourMode}
                      iconLeading={ClockIcon}
                    >
                      {t('resources.assistants.working_hours.set_24h')}
                    </Button>
                  )}
                </div>

                {!is24HourMode && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {t('resources.assistants.working_hours.start_time')}
                      </label>
                      <Input
                        type="time"
                        value={currentRule.recurrence_pattern?.start_time || '09:00'}
                        onChange={(value) => handleTimeChange('start_time', value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {t('resources.assistants.working_hours.end_time')}
                      </label>
                      <Input
                        type="time"
                        value={currentRule.recurrence_pattern?.end_time || '17:00'}
                        onChange={(value) => handleTimeChange('end_time', value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Date range for non-recurring */
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                  {t('resources.assistants.working_hours.start_date')}
                </h3>
                <Input
                  type="date"
                  value={currentRule.start_date?.split(' ')[0] || ''}
                  onChange={(value) => setCurrentRule({ ...currentRule, start_date: `${value} 00:00` })}
                />
                {errors.start_date && (
                  <p className="mt-1 text-sm text-error-600">{errors.start_date}</p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                  {t('resources.assistants.working_hours.end_date')}
                </h3>
                <Input
                  type="date"
                  value={currentRule.end_date?.split(' ')[0] || ''}
                  onChange={(value) => setCurrentRule({ ...currentRule, end_date: `${value} 23:59` })}
                />
                {errors.end_date && (
                  <p className="mt-1 text-sm text-error-600">{errors.end_date}</p>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button color="secondary" onPress={onClose}>
            {t('components.confirm_dialog.cancel')}
          </Button>
          <Button color="primary" onPress={handleSave}>
            {t('resources.assistants.working_hours.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default WorkingHoursModal
